import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAdmin } from '@/context/AdminContext';
import { useAdminTheme } from '@/context/AdminThemeContext';
import { adminService } from '@/services/adminApi';
import AdminSidebar from '@/components/admin/AdminSidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Users, Search, Trash2, RefreshCw, ChevronLeft, ChevronRight,
  Mail, Phone, Calendar, Download, Filter, X, Globe, Menu,
  FileSpreadsheet, MapPin, CheckCircle, XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

// Excel export utility
const exportToExcel = (data, filename) => {
  // Convert data to CSV
  const headers = Object.keys(data[0] || {});
  const csvContent = [
    headers.join(','),
    ...data.map(row => 
      headers.map(h => {
        const val = row[h] || '';
        // Escape commas and quotes
        if (typeof val === 'string' && (val.includes(',') || val.includes('"'))) {
          return `"${val.replace(/"/g, '""')}"`;
        }
        return val;
      }).join(',')
    )
  ].join('\n');

  // Create and download file
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
  link.click();
};

// Country colors for pie chart
const COUNTRY_COLORS = [
  '#5BA946', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444',
  '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#6366f1'
];

export default function AdminUsers() {
  const { isAuthenticated, loading: authLoading } = useAdmin();
  const { colors, isDark } = useAdminTheme();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Data states
  const [profiles, setProfiles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [countries, setCountries] = useState([]);
  const [countryStats, setCountryStats] = useState([]);
  
  // Filter states
  const [search, setSearch] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [hasEmail, setHasEmail] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(0);
  const limit = 20;
  
  // Mobile sidebar
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    if (isAuthenticated) {
      loadData();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      loadProfiles();
    }
  }, [isAuthenticated, page, search, selectedCountry, dateFrom, dateTo, hasEmail]);

  const loadData = async () => {
    try {
      const [countriesRes, countryStatsRes] = await Promise.all([
        adminService.getAvailableCountries(),
        adminService.getUsersByCountry()
      ]);
      setCountries(countriesRes.data.countries || []);
      setCountryStats(countryStatsRes.data || []);
    } catch (error) {
      console.error('Failed to load countries:', error);
    }
  };

  const loadProfiles = async () => {
    try {
      setLoading(true);
      const params = {
        limit,
        offset: page * limit,
        search: search || undefined,
        country: selectedCountry !== 'all' ? selectedCountry : undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        has_email: hasEmail === 'all' ? undefined : hasEmail === 'yes'
      };
      
      const res = await adminService.getProfilesFiltered(params);
      setProfiles(res.data.profiles || []);
      setTotal(res.data.total || 0);
    } catch (error) {
      toast.error('Failed to load profiles');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const params = {
        search: search || undefined,
        country: selectedCountry !== 'all' ? selectedCountry : undefined,
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined
      };
      
      const res = await adminService.exportProfiles(params);
      if (res.data.data && res.data.data.length > 0) {
        exportToExcel(res.data.data, 'edomatch_users');
        toast.success(`Exported ${res.data.total} users`);
      } else {
        toast.info('No data to export');
      }
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async (profileId, name) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"?`)) return;
    
    try {
      await adminService.deleteProfile(profileId);
      toast.success('Profile deleted');
      loadProfiles();
    } catch (error) {
      toast.error('Failed to delete profile');
    }
  };

  const clearFilters = () => {
    setSearch('');
    setSelectedCountry('all');
    setDateFrom('');
    setDateTo('');
    setHasEmail('all');
    setPage(0);
  };

  const hasActiveFilters = search || selectedCountry !== 'all' || dateFrom || dateTo || hasEmail !== 'all';

  if (authLoading) {
    return (
      <div className={`min-h-screen ${colors.bg} flex items-center justify-center`}>
        <div className="w-8 h-8 border-4 border-[#5BA946] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${colors.bg} flex`}>
      <AdminSidebar 
        currentPath={location.pathname}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      
      <main className="flex-1 overflow-auto">
        {/* Header */}
        <header className={`${colors.bgHeader} border-b ${colors.border} p-4 sm:p-6`}>
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Button
                onClick={() => setSidebarOpen(true)}
                variant="ghost"
                size="icon"
                className={`lg:hidden ${colors.textSecondary}`}
              >
                <Menu className="w-6 h-6" />
              </Button>
              <div>
                <h1 className={`text-xl sm:text-2xl font-bold ${colors.text}`}>Users & CVs</h1>
                <p className={`${colors.textSecondary} text-sm mt-1`}>{total} total profiles</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                onClick={handleExport}
                disabled={exporting}
                variant="outline"
                size="sm"
                className={`${colors.btnOutline} hidden sm:flex`}
              >
                <FileSpreadsheet className={`w-4 h-4 ${exporting ? 'animate-spin' : ''} mr-2`} />
                Export Excel
              </Button>
              <Button 
                onClick={loadProfiles}
                variant="outline"
                size="sm"
                className={colors.btnOutline}
              >
                <RefreshCw className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Refresh</span>
              </Button>
            </div>
          </div>
        </header>

        <div className="p-4 sm:p-6 space-y-6">
          {/* Country Distribution Chart */}
          {countryStats.length > 0 && (
            <Card className={`${colors.bgCard} ${colors.border}`}>
              <CardHeader>
                <CardTitle className={`${colors.text} flex items-center gap-2`}>
                  <Globe className="w-5 h-5 text-[#5BA946]" />
                  Users by Country
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={countryStats.slice(0, 10)}
                        dataKey="count"
                        nameKey="country"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ country, count }) => `${country}: ${count}`}
                      >
                        {countryStats.slice(0, 10).map((_, index) => (
                          <Cell key={index} fill={COUNTRY_COLORS[index % COUNTRY_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        contentStyle={{ 
                          backgroundColor: isDark ? '#1f2937' : '#fff',
                          border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
                          borderRadius: '8px'
                        }}
                      />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Filters Section */}
          <Card className={`${colors.bgCard} ${colors.border}`}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className={`${colors.text} text-lg flex items-center gap-2`}>
                  <Filter className="w-5 h-5" />
                  Filters
                </CardTitle>
                <div className="flex items-center gap-2">
                  {hasActiveFilters && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearFilters}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Clear
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                    className={colors.textSecondary}
                  >
                    {showFilters ? 'Hide' : 'Show'} Filters
                  </Button>
                </div>
              </div>
            </CardHeader>
            
            <CardContent className={showFilters ? '' : 'hidden sm:block'}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Search */}
                <div className="relative">
                  <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${colors.textMuted}`} />
                  <Input
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                    placeholder="Search name, email..."
                    className={`pl-10 ${colors.bgInput} ${colors.border} ${colors.text}`}
                  />
                </div>

                {/* Country Filter */}
                <Select value={selectedCountry} onValueChange={(v) => { setSelectedCountry(v); setPage(0); }}>
                  <SelectTrigger className={`${colors.bgInput} ${colors.border} ${colors.text}`}>
                    <MapPin className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="All Countries" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Countries</SelectItem>
                    {countries.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Date From */}
                <div>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => { setDateFrom(e.target.value); setPage(0); }}
                    placeholder="From date"
                    className={`${colors.bgInput} ${colors.border} ${colors.text}`}
                  />
                </div>

                {/* Date To */}
                <div>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => { setDateTo(e.target.value); setPage(0); }}
                    placeholder="To date"
                    className={`${colors.bgInput} ${colors.border} ${colors.text}`}
                  />
                </div>

                {/* Has Email Filter */}
                <Select value={hasEmail} onValueChange={(v) => { setHasEmail(v); setPage(0); }}>
                  <SelectTrigger className={`${colors.bgInput} ${colors.border} ${colors.text}`}>
                    <Mail className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Email Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    <SelectItem value="yes">With Email</SelectItem>
                    <SelectItem value="no">Without Email</SelectItem>
                  </SelectContent>
                </Select>

                {/* Mobile Export Button */}
                <Button 
                  onClick={handleExport}
                  disabled={exporting}
                  className="sm:hidden bg-[#5BA946] hover:bg-[#4a9339]"
                >
                  <Download className={`w-4 h-4 ${exporting ? 'animate-spin' : ''} mr-2`} />
                  Export Excel
                </Button>
              </div>

              {hasActiveFilters && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {search && (
                    <Badge variant="secondary" className="gap-1">
                      Search: {search}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => setSearch('')} />
                    </Badge>
                  )}
                  {selectedCountry !== 'all' && (
                    <Badge variant="secondary" className="gap-1">
                      Country: {selectedCountry}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => setSelectedCountry('all')} />
                    </Badge>
                  )}
                  {dateFrom && (
                    <Badge variant="secondary" className="gap-1">
                      From: {dateFrom}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => setDateFrom('')} />
                    </Badge>
                  )}
                  {dateTo && (
                    <Badge variant="secondary" className="gap-1">
                      To: {dateTo}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => setDateTo('')} />
                    </Badge>
                  )}
                  {hasEmail !== 'all' && (
                    <Badge variant="secondary" className="gap-1">
                      {hasEmail === 'yes' ? 'With Email' : 'Without Email'}
                      <X className="w-3 h-3 cursor-pointer" onClick={() => setHasEmail('all')} />
                    </Badge>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Users Table */}
          <Card className={`${colors.bgCard} ${colors.border}`}>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`border-b ${colors.border}`}>
                      <th className={`text-left p-4 ${colors.textSecondary} font-medium`}>User</th>
                      <th className={`text-left p-4 ${colors.textSecondary} font-medium hidden md:table-cell`}>Contact</th>
                      <th className={`text-left p-4 ${colors.textSecondary} font-medium hidden lg:table-cell`}>Country</th>
                      <th className={`text-left p-4 ${colors.textSecondary} font-medium hidden sm:table-cell`}>Created</th>
                      <th className={`text-right p-4 ${colors.textSecondary} font-medium`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={5} className={`p-8 text-center ${colors.textMuted}`}>
                          Loading...
                        </td>
                      </tr>
                    ) : profiles.length === 0 ? (
                      <tr>
                        <td colSpan={5} className={`p-8 text-center ${colors.textMuted}`}>
                          No profiles found
                        </td>
                      </tr>
                    ) : profiles.map((profile) => (
                      <tr key={profile.id} className={`border-b ${colors.border} ${colors.bgHover}`}>
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#5BA946] rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                              {profile.first_name?.[0] || profile.last_name?.[0] || '?'}
                            </div>
                            <div className="min-w-0">
                              <p className={`${colors.text} font-medium truncate`}>
                                {profile.first_name || profile.last_name 
                                  ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim()
                                  : 'Anonymous'}
                              </p>
                              <p className={`text-xs ${colors.textMuted} truncate`}>
                                {profile.target_job || 'No target job'}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="p-4 hidden md:table-cell">
                          <div className="space-y-1">
                            {profile.contact?.email ? (
                              <p className={`${colors.textSecondary} text-sm flex items-center gap-1`}>
                                <Mail className="w-3 h-3" />
                                <span className="truncate max-w-[200px]">{profile.contact.email}</span>
                              </p>
                            ) : (
                              <span className={`${colors.textMuted} text-sm`}>No email</span>
                            )}
                          </div>
                        </td>
                        <td className="p-4 hidden lg:table-cell">
                          <div className="flex items-center gap-1">
                            <Globe className={`w-3 h-3 ${colors.textMuted}`} />
                            <span className={colors.textSecondary}>
                              {profile.contact?.country || 'Unknown'}
                            </span>
                          </div>
                        </td>
                        <td className="p-4 hidden sm:table-cell">
                          <p className={`${colors.textSecondary} text-sm flex items-center gap-1`}>
                            <Calendar className="w-3 h-3" />
                            {profile.created_at ? new Date(profile.created_at).toLocaleDateString() : '-'}
                          </p>
                        </td>
                        <td className="p-4 text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(profile.id, `${profile.first_name} ${profile.last_name}`)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className={`flex items-center justify-between p-4 border-t ${colors.border}`}>
                <p className={`text-sm ${colors.textSecondary}`}>
                  {total > 0 ? `${page * limit + 1}-${Math.min((page + 1) * limit, total)} of ${total}` : '0 results'}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 0}
                    onClick={() => setPage(p => p - 1)}
                    className={colors.btnOutline}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={(page + 1) * limit >= total}
                    onClick={() => setPage(p => p + 1)}
                    className={colors.btnOutline}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
